import React from 'react';
// src/App.tsx
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import RegistrationPage from './pages/Registration';
import StaffIntakeQrPage from './pages/StaffIntakeQr';
import ReservePage from './pages/Reserve';
import LoginPage from './pages/Login'; // ← 名前はそのまま使う（中身を電話番号入力に変える）
import LoginMigrationPage from './pages/LoginMigration';
import StoreSetupPage from './pages/StoreSetup';
import ResetPasswordPage from './pages/ResetPassword';
import MessagesPage from './pages/Messages';
import SettingsPage from './pages/Settings';
import StaffPayslipPage from './pages/StaffPayslip';
import RequireAuth from './utils/RequireAuth';
import OfflineBanner from './components/OfflineBanner';
import UpdatePrompt from './components/UpdatePrompt';
import LaunchSplashScreen from './components/LaunchSplashScreen';
import { syncUnreadAppBadge } from './utils/appBadge';
import { I18nProvider } from './i18n';
import {
  getNotificationPermissionSafely,
  syncPushSubscriptionForCustomer,
} from './utils/pushNotifications';

type AppProps = {
  hasUpdate?: boolean;
  isApplyingUpdate?: boolean;
};

const LAUNCH_SPLASH_LAST_SHOWN_AT_KEY = 'customer_launch_splash_last_shown_at';
const LAUNCH_SPLASH_SKIP_WINDOW_MS = 8000;
const PUSH_SUBSCRIPTION_SYNC_INTERVAL_MS = 60_000;
const UNREAD_BADGE_POLL_INTERVAL_MS = 15_000;

const hasShownLaunchSplashRecently = () => {
  try {
    const lastShownAt = Number(window.localStorage.getItem(LAUNCH_SPLASH_LAST_SHOWN_AT_KEY) || 0);
    return Boolean(lastShownAt) && Date.now() - lastShownAt < LAUNCH_SPLASH_SKIP_WINDOW_MS;
  } catch {
    return false;
  }
};

const recordLaunchSplashShown = () => {
  try {
    window.localStorage.setItem(LAUNCH_SPLASH_LAST_SHOWN_AT_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures and continue normally.
  }
};

function App({
  hasUpdate = false,
  isApplyingUpdate = false,
}: AppProps) {
  return (
    <I18nProvider>
      <Router>
        <AppShell hasUpdate={hasUpdate} isApplyingUpdate={isApplyingUpdate} />
      </Router>
    </I18nProvider>
  );
}

const AppShell = ({
  hasUpdate,
  isApplyingUpdate,
}: AppProps) => {
  const location = useLocation();
  const isLaunchSplash = location.pathname === '/';
  const shouldSkipLaunchSplash = isLaunchSplash && hasShownLaunchSplashRecently();
  const lastPushSubscriptionSyncAtRef = React.useRef(0);

  const markLaunchSplashSeen = React.useCallback(() => {
    recordLaunchSplashShown();
  }, []);

  React.useEffect(() => {
    const syncBadge = () => {
      if (document.visibilityState === 'visible') {
        void syncUnreadAppBadge();
      }
    };

    void syncUnreadAppBadge();
    const onPushMessage = (event: MessageEvent) => {
      if (event.data?.type === 'CUSTOMER_MESSAGES_CHANGED') syncBadge();
    };

    navigator.serviceWorker?.addEventListener('message', onPushMessage);
    window.addEventListener('customer-messages:changed', syncBadge);
    window.addEventListener('focus', syncBadge);
    document.addEventListener('visibilitychange', syncBadge);
    const pollId = window.setInterval(syncBadge, UNREAD_BADGE_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(pollId);
      navigator.serviceWorker?.removeEventListener('message', onPushMessage);
      window.removeEventListener('customer-messages:changed', syncBadge);
      window.removeEventListener('focus', syncBadge);
      document.removeEventListener('visibilitychange', syncBadge);
    };
  }, [location.pathname]);

  React.useEffect(() => {
    const syncPushSubscription = () => {
      if (document.visibilityState !== 'visible') return;
      if (getNotificationPermissionSafely() !== 'granted') return;

      const customerId = Number(window.localStorage.getItem('customerId') || 0);
      if (!Number.isFinite(customerId) || customerId <= 0) return;

      const now = Date.now();
      if (now - lastPushSubscriptionSyncAtRef.current < PUSH_SUBSCRIPTION_SYNC_INTERVAL_MS) return;
      lastPushSubscriptionSyncAtRef.current = now;

      void syncPushSubscriptionForCustomer(customerId).catch((error) => {
        lastPushSubscriptionSyncAtRef.current = 0;
        console.error('Failed to sync push subscription on app foreground:', error);
      });
    };

    syncPushSubscription();
    window.addEventListener('focus', syncPushSubscription);
    document.addEventListener('visibilitychange', syncPushSubscription);

    return () => {
      window.removeEventListener('focus', syncPushSubscription);
      document.removeEventListener('visibilitychange', syncPushSubscription);
    };
  }, []);

  return (
    <>
      <OfflineBanner />
      <UpdatePrompt
        visible={!isLaunchSplash && Boolean(hasUpdate || isApplyingUpdate)}
        updating={Boolean(isApplyingUpdate)}
      />
      <Routes>
        <Route
          path="/"
          element={shouldSkipLaunchSplash ? <Navigate to="/reserve" replace /> : <LaunchSplashScreen onShown={markLaunchSplashSeen} />}
        />

        {/* 電話番号入力（旧 LoginPage） */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/migration" element={<LoginMigrationPage />} />
        <Route path="/login/store-setup" element={<StoreSetupPage />} />
        <Route path="/login/reset-password" element={<ResetPasswordPage />} />

        <Route path="/registration" element={<RegistrationPage />} />
        <Route path="/staff-intake-qr" element={<StaffIntakeQrPage />} />
        <Route path="/staff/payslips/:token" element={<StaffPayslipPage />} />

        <Route
          path="/reserve/*"
          element={(
            <RequireAuth>
              <ReservePage />
            </RequireAuth>
          )}
        />

        {/* Card は一旦保留。直接アクセス時は予約トップへ戻す */}
        <Route path="/card" element={<Navigate to="/reserve" replace />} />

        <Route
          path="/messages"
          element={(
            <RequireAuth>
              <MessagesPage />
            </RequireAuth>
          )}
        />

        <Route
          path="/settings"
          element={(
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          )}
        />
      </Routes>

      <ToastContainer position="top-right" autoClose={2000} />
    </>
  );
};

export default App;
